import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import debounce from 'lodash-es/debounce';
import { CircleHelpIcon, PlusIcon } from 'lucide-react';
import { Controller, Resolver, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { AuthoringValidationBanner } from '@/components/atoms/AuthoringValidationBanner/AuthoringValidationBanner';
import { GuidelineEditor } from '@/components/atoms/GuidelineEditor';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import ToolkitRuleNew from '@/components/organisms/Toolkit/ToolkitRule/ToolkitRule';
import ToolkitWrapper from '@/components/organisms/Toolkit/ToolkitWrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConditionValidator } from '@/hooks/useConditionValidator';
import useDialogAlert from '@/hooks/useDialogAlert';
import { clearDialogAlerts } from '@/redux/dialogAlerts/actions';
import { selectErrorByField } from '@/redux/dialogAlerts/selectors';
import { generateRule } from '@/redux/dialogs/helper';
import {
  selectConditionRules,
  selectConditionToolkit,
  selectMatchValue,
} from '@/redux/dialogs/selectors';
import {
  addRule,
  removeRule,
  updateCondition,
  updateConditionMatch,
  updateRule,
} from '@/redux/nodes/actions';
import { ConditionMatchOption, Rule } from '@/shared/types/node';
import { CONDITION_DOCS_URL } from '@/util/constants';
import { formatErrorMessage } from '@/util/util';
import { newConditionSchema } from '@/util/validation/schemas/dialog-schemas';

type FormType = { name: string; match: ConditionMatchOption['value']; guideline?: string };

const ToolkitNodeCondition = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const rules = useSelector(selectConditionRules, shallowEqual);
  const { nodeId, conditionIndex, name, matchOptions, conditionId, guidelineRule } =
    useSelector(selectConditionToolkit);

  const matchValue = useSelector(selectMatchValue);

  const {
    formState: { errors, dirtyFields },
    getValues,
    setValue,
    control,
  } = useForm<FormType>({
    mode: 'onChange',
    defaultValues: { name, match: matchValue, guideline: guidelineRule?.value },
    resolver: yupResolver(newConditionSchema) as Resolver<FormType>,
  });
  const nameAlert = useSelector(selectErrorByField(conditionId, 'name'));
  const guidelineAlert = useSelector(selectErrorByField(conditionId, 'guideline'));
  const nameErrorMessage = formatErrorMessage(errors.name);
  const guidelineErrorMessage = formatErrorMessage(errors.guideline);

  const [watchedMatch, watchedGuideline] = useWatch({ control, name: ['match', 'guideline'] });

  const showRules = watchedMatch !== 'else' && watchedMatch !== 'guideline';

  // Don't validate pre-loaded guideline text while the user is elsewhere (e.g. editing the
  // name). Only kick off once they focus the guideline editor. Resets when switching
  // conditions so each condition's first focus re-arms validation.
  const [hasInteractedWithGuideline, setHasInteractedWithGuideline] = useState(false);
  useEffect(() => {
    setHasInteractedWithGuideline(false);
  }, [conditionId]);
  const handleGuidelineFocus = useCallback(() => {
    setHasInteractedWithGuideline(true);
  }, []);

  const { status: validationStatus, recommendation: validationRecommendation } =
    useConditionValidator({
      matchIsGuideline: watchedMatch === 'guideline',
      guidelineValue: watchedGuideline ?? '',
      hasInteracted: hasInteractedWithGuideline,
    });

  // Set the form values when the selected condition changes
  // because the component is not unmounted
  useEffect(() => {
    setValue('name', name, { shouldValidate: true, shouldDirty: true });
    setValue('match', matchValue, { shouldValidate: true });
    setValue('guideline', guidelineRule?.value, { shouldValidate: true });
  }, [name, matchValue, setValue, guidelineRule, dirtyFields.guideline]);

  const update = useCallback(() => {
    const values = getValues();
    dispatch(
      updateCondition({
        nodeId,
        index: conditionIndex,
        condition: {
          ...values,
          // FIXME: Orchestrator expects `all` when using the `guideline` condition
          match: values.match === 'guideline' ? 'all' : values.match,
        },
      })
    );
  }, [dispatch, getValues, nodeId, conditionIndex]);

  const { setFieldError } = useDialogAlert({
    id: conditionId,
    nodeId,
    title: t('actions.types.condition'),
    type: 'condition',
    index: conditionIndex,
  });

  // Handle errors when the user is changing the fields
  useEffect(() => {
    setFieldError('name', nameErrorMessage);
    setFieldError('guideline', guidelineErrorMessage);
  }, [setFieldError, guidelineErrorMessage, nameErrorMessage]);

  const handleChange = useCallback(
    (_: ChangeEvent<HTMLInputElement>) => {
      update();
    },
    [update]
  );

  const onAddRuleClick = useCallback(() => {
    dispatch(addRule({ nodeId, conditionIndex, rule: generateRule() }));
  }, [dispatch, nodeId, conditionIndex]);

  const onRuleChange = useCallback(
    (rule: Partial<Rule>, index: number) => {
      const newRule = { ...rules[index], ...rule };

      dispatch(updateRule({ nodeId, conditionIndex, index, rule: { ...newRule } }));
    },
    [rules, dispatch, nodeId, conditionIndex]
  );

  const debouncedRuleChange = useMemo(
    () =>
      debounce((value: string) => {
        // A guideline rule is always the first and the only rule
        onRuleChange({ value }, 0);
      }, 200),
    [onRuleChange]
  );

  const handleGuidelineChange = useCallback(
    (value: string) => {
      setValue('guideline', value);
      debouncedRuleChange(value);
    },
    [debouncedRuleChange, setValue]
  );

  useEffect(() => {
    return () => {
      debouncedRuleChange.cancel();
    };
  }, [debouncedRuleChange]);

  const onRuleDelete = useCallback(
    (index: number) => {
      dispatch(removeRule({ nodeId, conditionIndex, index }));
    },
    [dispatch, nodeId, conditionIndex]
  );

  const handleMatchChange = useCallback(
    (value: string) => {
      setValue('match', value as ConditionMatchOption['value']);
      dispatch(updateConditionMatch({ nodeId, conditionIndex, match: value }));
      // Clear dialog alerts when the user is changing the match value as it is generating a new condition with valid values
      dispatch(clearDialogAlerts({ id: conditionId }));
    },
    [dispatch, nodeId, conditionIndex, conditionId, setValue]
  );

  const renderRules = useCallback(() => {
    return rules.map((r, index) => (
      <ToolkitRuleNew
        key={r.rule_id}
        onChange={onRuleChange}
        onDelete={rules.length === 1 ? undefined : onRuleDelete}
        index={index}
        rule={r}
        nodeId={nodeId}
        conditionId={conditionId}
        conditionIndex={conditionIndex}
      />
    ));
  }, [conditionId, conditionIndex, nodeId, onRuleChange, onRuleDelete, rules]);

  return (
    <ToolkitWrapper type="condition">
      <div className="mb-4">
        <Label className="mb-2">{t('common.name')}</Label>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              onChange={(event) => {
                field.onChange(event);
                handleChange(event);
              }}
              aria-invalid={!!nameAlert?.body}
            />
          )}
        />
        {nameAlert?.body && <p className="mt-1 text-xs text-destructive">{nameAlert?.body}</p>}
      </div>

      <div className="mb-4">
        <Label className="mb-2">
          {t('dialog.condition.match_when')}
          <InfoTooltip
            content={t('dialog.condition.match_when_tooltip')}
            learnMoreUrl={CONDITION_DOCS_URL}
            side="right"
          >
            <CircleHelpIcon size={12} className="text-muted-foreground" />
          </InfoTooltip>
        </Label>
        <Select value={watchedMatch} onValueChange={handleMatchChange}>
          <SelectTrigger>
            <SelectValue placeholder={t('dialog.condition.type_match')} />
          </SelectTrigger>
          <SelectContent>
            {matchOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                {t(option.label_key)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/*
        Hide the add rule button when match is set to 'else'
       */}
      {showRules && (
        <div className="mb-4 flex justify-end">
          <Button variant="ghost" size="sm" onClick={onAddRuleClick}>
            <PlusIcon />
            {t('rules.add_rule')}
          </Button>
        </div>
      )}

      {rules.length > 0 && showRules && <div>{renderRules()}</div>}
      {watchedMatch === 'guideline' && (
        <>
          <GuidelineEditor
            value={guidelineRule?.value || ''}
            onChange={handleGuidelineChange}
            onFocus={handleGuidelineFocus}
            label={t('dialog.condition.guideline')}
            placeholder={t('dialog.condition.guideline_placeholder')}
            errorMessage={
              dirtyFields?.match && dirtyFields?.guideline ? undefined : guidelineAlert?.body
            }
          />
          <p className="mt-2 text-sm text-muted-foreground">
            {t('dialog.condition.guideline_description')}
          </p>
          {validationStatus === 'validating' && (
            <p className="mt-2 text-xs text-muted-foreground" role="status" aria-live="polite">
              {t('dialog.condition.guideline_validating')}
            </p>
          )}
          {validationRecommendation && (
            <div className="mt-2">
              <AuthoringValidationBanner recommendation={validationRecommendation} />
            </div>
          )}
        </>
      )}
    </ToolkitWrapper>
  );
};

export default ToolkitNodeCondition;
